import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "../../../api";
import { MIGRATION } from "../../../constants/testIds";
import { Input } from "../../ui/input";
import { Badge } from "../../ui/badge";
import { SubmitButton } from "../../common/SubmitButton";

const STATUS_TONE = { sent: "#C9A227", authorized: "#3FA65B" };

// SCR-P04-20 - search members, build a re-mandate campaign, track per-member link_status
// (sent -> authorized). `member.view` (not migration.manage alone) is what lets this tab search.
export function RemandateTab() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]); // [{id, name, phone}]

  const { data: matches } = useQuery({
    queryKey: ["remandate-member-search", search],
    queryFn: () => api.get("/members", { params: /^\d+$/.test(search) ? { phone: search } : { name: search } }).then((r) => r.data.members),
    enabled: search.length >= 2,
  });

  const { data: campaigns } = useQuery({
    queryKey: ["remandate-campaigns"],
    queryFn: () => api.get("/remandate-campaigns").then((r) => r.data.campaigns),
  });

  const sendMutation = useMutation({
    mutationFn: () => api.post("/remandate/campaign", { member_refs: selected.map((m) => m.id) }),
    onSuccess: () => { toast.success("Re-authorization campaign sent"); setSelected([]); queryClient.invalidateQueries({ queryKey: ["remandate-campaigns"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Could not send campaign"),
  });

  const authorizeMutation = useMutation({
    mutationFn: ({ campaignId, memberRef }) => api.post(`/remandate/${campaignId}/authorize`, { member_ref: memberRef }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["remandate-campaigns"] }),
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Could not authorize"),
  });

  const toggle = (m) => setSelected((prev) => (prev.some((x) => x.id === m.id) ? prev.filter((x) => x.id !== m.id) : [...prev, m]));

  return (
    <div className="space-y-4">
      <div className="solid-surface rounded-[14px] p-5 space-y-3">
        <p className="text-sm font-semibold" style={{ color: "var(--scarlet-text)" }}>Re-authorization campaign</p>
        <Input data-testid={MIGRATION.remandateMemberSearch} placeholder="Search member by name or phone" value={search} onChange={(e) => setSearch(e.target.value)} />
        {matches?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {matches.map((m) => (
              <button
                key={m.id} data-testid={MIGRATION.remandateMemberOption(m.id)} onClick={() => toggle(m)}
                className="text-xs rounded-full px-3 py-1 border" style={{ borderColor: "var(--scarlet-hairline)", color: "var(--scarlet-text)" }}
              >
                {m.profile?.name || m.name} &middot; {m.profile?.phone || m.phone}
              </button>
            ))}
          </div>
        )}
        {selected.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {selected.map((m) => <Badge key={m.id} data-testid={MIGRATION.remandateSelectedChip(m.id)}>{m.profile?.name || m.name}</Badge>)}
          </div>
        )}
        <SubmitButton testId={MIGRATION.remandateSendButton} disabled={selected.length === 0} pending={sendMutation.isPending} onClick={() => sendMutation.mutate()} label={`Send campaign (${selected.length})`} />
      </div>

      <div className="space-y-2">
        <p className="text-sm font-semibold" style={{ color: "var(--scarlet-text)" }}>Past campaigns</p>
        {(!campaigns || campaigns.length === 0) && <p className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>No campaigns yet.</p>}
        {campaigns?.map((c) => (
          <div key={c.id} data-testid={MIGRATION.remandateCampaignRow(c.id)} className="solid-surface rounded-[12px] p-3 space-y-1.5">
            <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>{new Date(c.at).toLocaleString()} &middot; {c.members.length} members</p>
            {c.members.map((m) => (
              <div key={m.member_ref} className="flex items-center justify-between text-sm">
                <span style={{ color: "var(--scarlet-text)" }}>{m.member_ref}</span>
                <div className="flex items-center gap-2">
                  <Badge style={{ background: `${STATUS_TONE[m.link_status]}22`, color: STATUS_TONE[m.link_status], border: "none" }}>{m.link_status}</Badge>
                  {m.link_status !== "authorized" && (
                    <button
                      data-testid={MIGRATION.remandateAuthorizeButton(m.member_ref)}
                      onClick={() => authorizeMutation.mutate({ campaignId: c.id, memberRef: m.member_ref })}
                      className="text-xs underline" style={{ color: "var(--scarlet-accent)" }}
                    >
                      Mark authorized
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
